















































































































































import Card from "@/components/UI/Card.vue";
import LocalData from "@/mixins/local-data";
import {Component, Mixins} from "vue-property-decorator";
import Http from '../mixins/http';
import Toast from '../mixins/toast';
import MarketSelect from "@/components/MarketSelect.vue";
import ProductLineDirectoryFiles from "@/components/ProductLineDirectoryFiles.vue";
import {SelectMarket} from "@/interfaces/global-data";
import {
  DirectoriesSynchronizationStatus,
  isDirectoriesSyncInProgress,
  ProductLineRecommendationSearchParams,
} from "@/interfaces/server-data";
import {downloadBlobAsFile} from "@/utils/utils";
import moment from "moment";

const SYNC_POLL_INTERVAL_MS = 3000;
const MARKET_STORAGE_KEY = 'directory_files_market';

@Component({
  components: {
    MarketSelect,
    ProductLineDirectoryFiles,
    Card
  },
})
export default class ProductLineDirectory extends Mixins(Http, Toast, LocalData) {
  selectMarket: SelectMarket | null = this.readSelectedMarketFromStorage();

  productLineDirectoryFile: File | null = null;
  filesRefreshKey: number = 0;

  useProductLineDirectoryOption: boolean = true;

  approveProductLinesModal: boolean = false;
  isSyncBlockingModalVisible: boolean = false;
  syncPollHandle: number | null = null;

  searchProductLineParamsModal: boolean = false;
  isSearchBlockingModalVisible: boolean = false;
  nameThresholdProductLineModal: number | null = null;
  priceThresholdProductLineModal: number | null = null;

  tableLoader = {
    counter: 0,
    acquire() { this.counter++ },
    release() { this.counter-- },
    isLoading() { return this.counter > 0 },
    reset() { this.counter = 0 },
  };

  readSelectedMarketFromStorage(): SelectMarket | null {
    const raw = localStorage.getItem(MARKET_STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as SelectMarket;
    } catch (e) {
      return null;
    }
  }

  updateSelectMarket(market: SelectMarket) {
    this.selectMarket = market;
    localStorage.setItem(MARKET_STORAGE_KEY, JSON.stringify(market));
    this.tableLoader.reset();
    this.stopSyncPolling();
    this.isSyncBlockingModalVisible = false;
    this.refreshMarketDependentState();
  }

  get isAnyOperationBlocked(): boolean {
    return !this.selectMarket
        || this.tableLoader.isLoading()
        || this.isSyncBlockingModalVisible;
  }

  get disableFileInput(): boolean {
    return this.isAnyOperationBlocked;
  }

  get disableUploadButton(): boolean {
    return !this.productLineDirectoryFile || this.isAnyOperationBlocked;
  }

  get disableExportButton(): boolean {
    return this.isAnyOperationBlocked;
  }

  get disableProductLineRecommendationSearch(): boolean {
    return this.isAnyOperationBlocked || this.isSearchBlockingModalVisible;
  }

  get disableApproveProductLinesButton(): boolean {
    return this.isAnyOperationBlocked || !this.useProductLineDirectoryOption;
  }

  showApproveProductLinesModal() {
    this.approveProductLinesModal = true;
  }

  uploadProductLineDirectoryFile() {
    if (!this.productLineDirectoryFile) {
      this.showError(this.$t('shared.messages.selectFile') as string);
      return;
    }
    if (!this.selectMarket) {
      this.showError(this.$t('shared.messages.selectTradingCampaign') as string);
      return;
    }
    const uploadFile = new FormData();
    uploadFile.append(
        'file',
        new Blob([this.productLineDirectoryFile]),
        this.productLineDirectoryFile.name
    );
    this.tableLoader.acquire();
    this.httpUploadProductLineDirectoryFile(this.selectMarket.id, uploadFile)
        .then((r) => {
          const serverMessage = typeof r.data === 'string' && r.data ? r.data : null;
          this.showInfo(serverMessage || (this.$t('masterData.productLine.uploadSuccess') as string));
          this.productLineDirectoryFile = null;
          this.filesRefreshKey++;
        })
        .catch(() => {
          this.showError(this.$t('masterData.productLine.uploadError') as string);
          this.productLineDirectoryFile = null;
        })
        .finally(() => {
          this.tableLoader.release();
        });
  }

  downloadProductLineDirectoryFile() {
    if (!this.selectMarket) {
      this.showError(this.$t('shared.messages.selectTradingNetwork') as string);
      return;
    }
    this.tableLoader.acquire();
    this.httpExportProductLineDirectory(this.selectMarket.id)
        .then((response) => {
          const fileNameFromHeader = this.extractFileNameFromContentDisposition(
              response.headers && response.headers['content-disposition']
          );
          const fallback = (this.$t('masterData.productLine.exportFileName') as string)
              + '_' + (this.selectMarket && this.selectMarket.tradingName)
              + '_' + moment().format('YYYYMMDD_HHmmss') + '.xlsx';
          downloadBlobAsFile(response.data, fileNameFromHeader || fallback);
        })
        .catch(() => {})
        .finally(() => {
          this.tableLoader.release();
        });
  }

  extractFileNameFromContentDisposition(header: string | undefined | null): string | null {
    if (!header) return null;
    const utf8Match = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      try {
        return decodeURIComponent(utf8Match[1].replace(/^"|"$/g, ''));
      } catch (e) {
        return utf8Match[1].replace(/^"|"$/g, '');
      }
    }
    const asciiMatch = header.match(/filename\s*=\s*"?([^";]+)"?/i);
    if (asciiMatch && asciiMatch[1]) {
      return asciiMatch[1];
    }
    return null;
  }

  approveProductLines() {
    if (!this.selectMarket) {
      this.showError(this.$t('shared.messages.selectTradingNetwork') as string);
      return;
    }
    this.httpSyncProductLinesToAssortment(this.selectMarket.id)
        .then(() => {
          this.isSyncBlockingModalVisible = true;
          this.startSyncPolling();
        })
        .catch((e) => {
          if (e && e.response && e.response.status === 409) {
            this.isSyncBlockingModalVisible = true;
            this.startSyncPolling();
          }
        });
  }

  startSyncPolling() {
    if (this.syncPollHandle !== null) {
      return;
    }
    this.pollSyncStatusOnce();
    this.syncPollHandle = window.setInterval(
        () => this.pollSyncStatusOnce(),
        SYNC_POLL_INTERVAL_MS
    );
  }

  pollSyncStatusOnce() {
    if (!this.selectMarket) {
      this.stopSyncPolling();
      this.isSyncBlockingModalVisible = false;
      return;
    }
    this.httpGetSyncProductLinesStatus(this.selectMarket.id)
        .then((r) => this.handleSyncStatus(r.data as DirectoriesSynchronizationStatus))
        .catch(() => {
          this.stopSyncPolling();
          this.isSyncBlockingModalVisible = false;
        });
  }

  handleSyncStatus(status: DirectoriesSynchronizationStatus) {
    if (status === DirectoriesSynchronizationStatus.DONE) {
      const wasRunning = this.isSyncBlockingModalVisible;
      this.stopSyncPolling();
      this.isSyncBlockingModalVisible = false;
      if (wasRunning) {
        this.showInfo(this.$t('masterData.productLine.approveSuccess') as string);
      }
    } else if (status === DirectoriesSynchronizationStatus.FAILED) {
      this.stopSyncPolling();
      this.isSyncBlockingModalVisible = false;
      this.showError(this.$t('masterData.productLine.approveError') as string);
    } else if (!isDirectoriesSyncInProgress(status)) {
      this.stopSyncPolling();
      this.isSyncBlockingModalVisible = false;
    }
  }

  stopSyncPolling() {
    if (this.syncPollHandle !== null) {
      window.clearInterval(this.syncPollHandle);
      this.syncPollHandle = null;
    }
  }

  refreshMarketDependentState() {
    if (!this.selectMarket) {
      return;
    }
    this.httpGetSyncProductLinesStatus(this.selectMarket.id)
        .then((r) => {
          const status = r.data as DirectoriesSynchronizationStatus;
          if (isDirectoriesSyncInProgress(status)) {
            this.isSyncBlockingModalVisible = true;
            this.startSyncPolling();
          }
        })
        .catch(() => {});
  }

  onClickProductLineSearchButton() {
    this.searchProductLineParamsModal = true;
  }

  async startProductLineRecommendationSearch() {
    if (!this.selectMarket) {
      this.showError(this.$t('shared.messages.selectTradingNetwork') as string);
      return;
    }
    const params: ProductLineRecommendationSearchParams = {
      tradingId: this.selectMarket.id,
      groupIds: null,
      categoryIds: null,
      subcategoryIds: null,
      nameThreshold: this.nameThresholdProductLineModal || 0,
      priceThreshold: this.priceThresholdProductLineModal || 0,
    };
    this.isSearchBlockingModalVisible = true;

    try {
      const response = await this.httpProductLineRecommendationSearchStart(params);

      this.searchProductLineParamsModal = false;
      this.nameThresholdProductLineModal = null;
      this.priceThresholdProductLineModal = null;

      downloadBlobAsFile(
          new Blob([response.data], { type: response.headers['content-type'] }),
          'product_line_report.xlsx'
      );

      this.showInfo(this.$t('masterData.productLine.recommendationFileSuccess') as string);
    } catch (e) {
      this.showError(this.$t('masterData.productLine.errorSearchRecommendations') as string);
    } finally {
      this.isSearchBlockingModalVisible = false;
    }
  }

  fetchUseProductLineDirectoryOption() {
    this.httpGetGlobalSettings()
        .then((r) => {
          this.useProductLineDirectoryOption = !!r.data.useProductLineDirectory;
        })
        .catch(() => {});
  }

  mounted() {
    this.fetchUseProductLineDirectoryOption();
    this.refreshMarketDependentState();
  }

  beforeDestroy() {
    this.stopSyncPolling();
  }
}
